import { useState, useRef, useCallback, useEffect, useContext } from 'react'
import { getOpenBetsCount } from '../adapters/kambi'
import { AuthContext } from '../context/authProvider'
import {
  loadFromStorage,
  saveToStorage,
  shouldUseCache,
} from '../utils/openBetsStorage/openBetsStorage'

export const useOpenBetsState = ({ isSportsEnabled, skipInitialFetch = false }) => {
  const { isLoggedIn } = useContext(AuthContext)
  
  const [openBetsCount, setOpenBetsCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isInitialized, setIsInitialized] = useState(false)

  const lastFetchTime = useRef(0)
  const cachedCount = useRef(0)

  const updateCache = useCallback((count) => {
    cachedCount.current = count
    lastFetchTime.current = Date.now()
    saveToStorage(count)
  }, [])

  const handleSuccess = useCallback(
    (data) => {
      setOpenBetsCount(data)
      updateCache(data)
    },
    [updateCache]
  )

  const handleError = useCallback((errorMessage) => {
    setError(errorMessage)
    if (cachedCount.current > 0) {
      setOpenBetsCount(cachedCount.current)
    }
  }, [])

  const handleSportsDisabled = useCallback(() => {
    setOpenBetsCount(0)
    setIsInitialized(true)
  }, [])

  const fetchOpenBetsCount = useCallback(
    async (force = false) => {
      if (!isSportsEnabled) {
        handleSportsDisabled()
        return
      }

      if (shouldUseCache(lastFetchTime.current, cachedCount.current, force)) {
        setOpenBetsCount(cachedCount.current)
        setIsInitialized(true)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const { ok, data, error: apiError } = await getOpenBetsCount()

        if (ok) {
          handleSuccess(data)
        } else {
          handleError(apiError)
        }
      } catch (err) {
        handleError(err.message)
      } finally {
        setIsLoading(false)
        setIsInitialized(true)
      }
    },
    [
      isSportsEnabled,
      handleSuccess,
      handleError,
      handleSportsDisabled,
    ]
  )

  const refreshOpenBetsCount = useCallback(() => {
    return fetchOpenBetsCount(true)
  }, [fetchOpenBetsCount])

  const initializeFromStorage = useCallback(() => {
    const storedCount = loadFromStorage()
    if (storedCount !== null) {
      setOpenBetsCount(storedCount)
      cachedCount.current = storedCount
      lastFetchTime.current = Date.now()
      setIsInitialized(true)
    }
  }, [])

  useEffect(() => {
    initializeFromStorage()
    
    if (!isLoggedIn) {
      setOpenBetsCount(0)
      setIsInitialized(true)
      return
    }
    
    if (skipInitialFetch) {
      setIsInitialized(true)
      return
    }
    
    fetchOpenBetsCount()
  }, [isLoggedIn, initializeFromStorage, fetchOpenBetsCount, skipInitialFetch])

  return {
    openBetsCount,
    isLoading,
    error,
    isInitialized,
    fetchOpenBetsCount,
    refreshOpenBetsCount,
    initializeFromStorage,
  }
}
